import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { t } from "@wr/i18n";
import { BrandLockup, Button, FormBanner, Mascot, WordLoader } from "@wr/ui";
import { BuildFooter } from "../components/BuildFooter";
import { fetchApiVersion } from "../lib/players";
import { useSession } from "../app/SessionProvider";
import { destinationFor, sanitiseNext } from "../app/routing";

/**
 * Splash — design spec §5. **It is a router, not a destination.**
 *
 * ── The anti-flash rule, LOCKED ─────────────────────────────────────────────
 *
 * If auth resolves in under 250ms, **nothing renders at all** — the player
 * goes straight through. A brand moment that appears for 90ms and vanishes
 * reads as a bug, not as personality. That is why `visible` is a timer and not
 * `status === "loading"`.
 *
 * The states after that are `booting` (mascot + wordmark + loader), `slow`
 * (>1200ms, adds a reassuring line), `error` (the check failed or ran past 8s)
 * and `exiting` (fade + 8px rise, then navigate).
 */

const REVEAL_MS = 250;
const SLOW_MS = 1200;
const TIMEOUT_MS = 8000;
const EXIT_MS = 180;

export interface SplashScreenProps {
  /**
   * Whether this instance owns the routing decision.
   *
   * `/` does. The copies that `RequireStage` renders while the session is
   * still resolving do **not** — the guard redirects on its own the moment the
   * answer arrives, and two things navigating on the same tick is a race with
   * no upside.
   */
  routeOnResolve?: boolean;
}

export function SplashScreen({ routeOnResolve = true }: SplashScreenProps) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { status, stage, reload } = useSession();

  const [visible, setVisible] = useState(false);
  const [slow, setSlow] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [apiCommit, setApiCommit] = useState<string | null>(null);

  const next = sanitiseNext(params.get("next"));

  useEffect(() => {
    const reveal = setTimeout(() => setVisible(true), REVEAL_MS);
    const slowTimer = setTimeout(() => setSlow(true), SLOW_MS);
    return () => {
      clearTimeout(reveal);
      clearTimeout(slowTimer);
    };
  }, []);

  /**
   * The 8s failure cut-off, armed only while the answer is outstanding.
   *
   * Keyed on `status` rather than fired unconditionally on mount: a boot that
   * legitimately takes seven seconds must not flip to an error banner one
   * second after it succeeded.
   */
  useEffect(() => {
    if (status !== "loading") return;
    const timeout = setTimeout(() => setTimedOut(true), TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [status]);

  // The api's own build SHA for the footer's second labelled value (N7). It is
  // deliberately not part of the boot gate: a footer must never hold up a boot.
  useEffect(() => {
    void fetchApiVersion().then((version) => setApiCommit(version?.commit ?? null));
  }, []);

  /**
   * Route as soon as the session is known, through the LOCKED matrix.
   *
   * `exiting` is deliberately NOT a dependency. It was, and that was a real
   * bug: `setExiting(true)` re-ran the effect, whose cleanup cancelled the
   * navigation timer it had just armed, and the splash sat on the loader
   * forever. The state is a render flag for the exit animation; the effect
   * keys on the *answer*, which is what actually changed.
   */
  useEffect(() => {
    if (!routeOnResolve || status !== "resolved") return;
    const destination = destinationFor(stage, next);
    setExiting(true);
    const timer = setTimeout(() => void navigate(destination, { replace: true }), EXIT_MS);
    return () => clearTimeout(timer);
  }, [status, stage, next, navigate, routeOnResolve]);

  const failed = status === "error" || timedOut;

  if (!visible && !failed) return null;

  return (
    <div
      className={`wr-splash${exiting ? " wr-splash--exiting" : ""}`}
      data-testid="splash"
      data-state={failed ? "error" : slow ? "slow" : "booting"}
    >
      <div className="wr-splash__block">
        <Mascot size={140} />
        <BrandLockup
          size="lg"
          orientation="stacked"
          showMascot={false}
          wordy={t("app.wordy")}
          royale={t("app.royale")}
          appName={t("app.name")}
        />

        {failed ? (
          <div className="wr-form-column wr-stack" style={{ gap: 16 }}>
            {/* Not a form: the banner announces, but it does not steal focus
                from the retry button that is the only thing to do here. */}
            <FormBanner tone="error" testId="splash-error" takeFocus={false}>
              {t("splash.error")}
            </FormBanner>
            <Button
              variant="primary"
              size="lg"
              fullWidth
              data-testid="splash-retry"
              onClick={() => {
                setTimedOut(false);
                void reload();
              }}
            >
              {t("splash.retry")}
            </Button>
          </div>
        ) : (
          <>
            <WordLoader label={t("splash.loading")} />
            {/* `slow` only — an immediate reassurance is an admission. */}
            {slow ? (
              <p className="wr-body-sm wr-ink-70" data-testid="splash-slow">
                {t("splash.slow")}
              </p>
            ) : null}
          </>
        )}
      </div>

      <BuildFooter apiCommit={apiCommit} />
    </div>
  );
}
